import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FormErrorMessage } from "../../../components";
import { ThreeWayCallReDialRequest } from "../../../models/api/Call";
import { DialPad } from '../../../components';
import { AssetSvg } from '../../../assets';
import Select, { components, OptionProps, StylesConfig } from "react-select";
import ReactCountryFlag from "react-country-flag";
import { useDispatch } from 'react-redux';
import { CallStatusType } from "../../../configs";
import { ActionAudioCall, useAppSelector, VRIAppStateType } from "../../../core";
import { AudioConferenceCalls } from "../../../models";
import { normalizeConferenceParticipantNumber } from "../../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callinfo: any;
  onCallConferenceParticipant: (value: ThreeWayCallReDialRequest) => Promise<any>;
  handleDisconnect: Function;
  audioConferenceCalls: AudioConferenceCalls;
  uiOnly?: boolean;
  onDialed?: (phoneNumber: string, displayName?: string) => void;
};

type SupportedCountryOption = {
  value: "US" | "AE" | "LB";
  label: string;
  countryCode: string;
  dialCode: string;
  localLength: number;
};

const COUNTRY_OPTIONS: SupportedCountryOption[] = [
  { value: "US", label: "United States", countryCode: "US", dialCode: "+1", localLength: 10 },
  { value: "AE", label: "UAE", countryCode: "AE", dialCode: "+971", localLength: 9 },
  { value: "LB", label: "Lebanon", countryCode: "LB", dialCode: "+961", localLength: 8 },
];

const CountryFlagOption = (props: OptionProps<SupportedCountryOption, false>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-center">
      <ReactCountryFlag
        countryCode={props.data.countryCode}
        svg
        style={{ width: "1.2em", height: "1.2em" }}
        title={props.data.label}
      />
      <span className="ml-2">{props.data.label}</span>
    </div>
  </components.Option>
);

const countrySelectStyles: StylesConfig<SupportedCountryOption, false> = {
  container: (base) => ({
    ...base,
    width: 64,
    marginRight: 8,
  }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    height: 34,
    backgroundColor: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "0.25rem",
    boxShadow: "none",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 0 0 10px",
    justifyContent: "center",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text)",
    padding: "0 8px 0 4px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "var(--text)",
  }),
  menu: (base) => ({
    ...base,
    width: 180,
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    border: "1px solid var(--border)",
  }),
};

export function ModalOutboundCallDial({
  show,
  handleShow,
  callinfo,
  onCallConferenceParticipant,
  handleDisconnect,
  audioConferenceCalls,
  uiOnly = false,
  onDialed
  }: Props) {

  const [dialedInput, setDialedInput] = useState('');
  const [dialedName, setDialedName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<SupportedCountryOption>(COUNTRY_OPTIONS[0]);
  const { register, handleSubmit, formState: { errors }, setValue, setError, clearErrors } = useForm<any>();

  const dispatch = useDispatch();

  const data = useAppSelector((state: VRIAppStateType) => ({
    audioConferenceCalls: state.audioCall.audioConferenceCalls
  }));

  // useEffect(() => {
  //   console.log("audioConferenceCalls updated:", data.audioConferenceCalls);
  // }, [data.audioConferenceCalls]);




  const dialedInputStyle = {
    minWidth:
      dialedInput.length > 25 ? dialedInput.length * 10 + 'px' : '180px',
  };

  function formatSelectedPhoneNumber(localDigits: string): string {
    return `${selectedCountry.dialCode} ${localDigits}`;
  }

  function normalizePhoneNumber(localDigits: string): string {
    return normalizeConferenceParticipantNumber(
      localDigits,
      selectedCountry.dialCode.replace("+", "")
    );
  }

  function validatePhoneNumber(localDigits: string): boolean {
    return new RegExp(`^\\d{${selectedCountry.localLength}}$`).test(localDigits);
  }

  function getValidationMessage(): string {
    return `${selectedCountry.label} phone numbers must contain exactly ${selectedCountry.localLength} digits after ${selectedCountry.dialCode}`;
  }

  function getActiveCallByNumber(normalizedNumber: string): { index: number; displayName?: string } | null {
    // console.log("audioConferenceCalls:", audioConferenceCalls);

    const completedStatuses = [
      CallStatusType.OutboundCallCompleted,
      CallStatusType.OutboundCallNotAnswered,
      CallStatusType.Failed,
    ];

    const index = audioConferenceCalls.findIndex((call) => {
      return (
        call.calledNumber === normalizedNumber &&
        call.status?.current !== undefined &&
        !completedStatuses.includes(call.status.current)
      );
    });

    if (index === -1) return null;
    return { index, displayName: audioConferenceCalls[index].displayName };
  }


  useEffect(() => {
    clearErrors();
  }, [dialedInput, selectedCountry, clearErrors])

  useEffect(() => {
    setSelectedCountry(COUNTRY_OPTIONS[0]);
    setDialedInput('');
    setDialedName('');
    clearErrors();
  }, [callinfo?.customParameters?.get("CallSid"), clearErrors]);

  const onSubmitData = () => {

    if (!validatePhoneNumber(dialedInput)) {
      setError("phoneNumber", {
        type: "manual",
        message: getValidationMessage(),
      });
      return;
    }
    const normalizedNumber = normalizePhoneNumber(dialedInput);
    const activeCall = getActiveCallByNumber(normalizedNumber);
    if (activeCall) {
      const calleeIndex = activeCall.index + 1;
      const calleeLabel = activeCall.displayName
        ? `#${calleeIndex} ${activeCall.displayName}`
        : `#${calleeIndex} callee ${calleeIndex}`;
      setError("phoneNumber", {
        type: "manual",
        message: `The number ${formatSelectedPhoneNumber(dialedInput)} (${calleeLabel}) is already in a call`,
      });
      return;
    }

    clearErrors("phoneNumber");
    handleShow(false);

      const request: ThreeWayCallReDialRequest = {
        phoneNumber: dialedInput,
        countryCode: selectedCountry.dialCode.replace("+", ""),
        callSid: callinfo.customParameters?.get("CallSid"),
      };
      const trimmedName = dialedName.trim();
      onDialed?.(normalizedNumber, trimmedName || undefined);
      if (!uiOnly) {
        dispatch(ActionAudioCall.UpsertAudioConferenceCall({
          calledNumber: normalizedNumber,
          displayName: trimmedName || undefined,
          status: {
            current: CallStatusType.OutboundCallInitiated,
          }
        }));
      }
      setDialedInput("");
      setDialedName("");
      onCallConferenceParticipant(request)
        .then((response) => {
          if (response || uiOnly) return;

          dispatch(ActionAudioCall.UpdateAudioConferenceCallStatus({
            calledNumber: normalizedNumber,
            current: CallStatusType.Failed,
            callSid: "",
          }));
        })
        .catch(() => {
          if (!uiOnly) {
            dispatch(ActionAudioCall.UpdateAudioConferenceCallStatus({
              calledNumber: normalizedNumber,
              current: CallStatusType.Failed,
              callSid: "",
            }));
          }
        });
  };

  return (
    <Modal show={show} onHide={() => { setDialedInput(''); setDialedName(''); handleShow(false); clearErrors() }} centered>
      <form onSubmit={handleSubmit(onSubmitData)}>
        <Modal.Header closeButton>
          <Modal.Title>Call Participant</Modal.Title>
        </Modal.Header>

        <Modal.Body className="dialer-modal-body">
          <div className="dialer-modal">
            <div className="d-flex flex-column justify-content-center align-items-center dialer-content">
              <input
                type="text"
                value={dialedName}
                onChange={(e) => setDialedName(e.target.value)}
                placeholder="Name (optional)"
                className="form-control mb-2"
                style={{ width: '280px' }}
              />
              {errors["phoneNumber"] && (
                <FormErrorMessage message={errors["phoneNumber"]?.message as string || "This field is required"} />
              )}
              <DialPad
                dialedInput={dialedInput}
                dialedInputStyle={dialedInputStyle}
                setDialedInput={setDialedInput}
                handleDisconnect={handleDisconnect}
                prefixLabel={selectedCountry.dialCode}
                maxLength={selectedCountry.localLength}
                allowSpecialChars={false}
                leftAddon={
                  <Select<SupportedCountryOption, false>
                    aria-label="Country selector"
                    isSearchable={false}
                    value={selectedCountry}
                    options={COUNTRY_OPTIONS}
                    placeholder={
                      <ReactCountryFlag
                        countryCode={COUNTRY_OPTIONS[0].countryCode}
                        svg
                        style={{ width: "1.2em", height: "1.2em" }}
                        title={COUNTRY_OPTIONS[0].label}
                      />
                    }
                    styles={countrySelectStyles}
                    components={{
                      Option: CountryFlagOption,
                    }}
                    formatOptionLabel={(option, { context }) =>
                      context === "value" ? (
                        <ReactCountryFlag
                          countryCode={option.countryCode}
                          svg
                          style={{ width: "1.2em", height: "1.2em" }}
                          title={option.label}
                        />
                      ) : (
                        <div className="d-flex align-items-center">
                          <ReactCountryFlag
                            countryCode={option.countryCode}
                            svg
                            style={{ width: "1.2em", height: "1.2em" }}
                            title={option.label}
                          />
                          <span className="ml-2">{option.label}</span>
                        </div>
                      )
                    }
                    onChange={(option) => {
                      if (!option) return;
                      setSelectedCountry(option);
                      setDialedInput('');
                    }}
                  />
                }
              />
              <div className="d-flex flex-row">
                <div className="key-row dial-button mt-2">
                  <button
                    type="submit"
                    className="btn mx-4 callPage-controlBtn btn-green outbound-call-submit"
                    onClick={() => {
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
                    </svg>
                    <span>Call</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

      </form>
    </Modal>
  );
}
